import { useState, useEffect } from 'react';
import { merchantAPI, integrationsAPI, channelsAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Bot, Save, RotateCcw, Sparkles, AlertTriangle, Info, Plus, Trash2, ShoppingCart, CalendarDays, ClipboardList, Headphones, BookOpen, Link2, X, Edit3, Star, Lock, Image, Film, Mic, FileText, File, FileSpreadsheet, HelpCircle, CheckCircle, ChevronDown, ChevronRight, Zap, Database, MessageSquare, Settings } from 'lucide-react';
import toast from 'react-hot-toast';

const BUSINESS_TYPES = {
  order: { label: 'Order / E-Commerce', Icon: ShoppingCart },
  appointment: { label: 'Appointment / Booking', Icon: CalendarDays },
  lead: { label: 'Lead / Inquiry', Icon: ClipboardList },
  support: { label: 'Support / Helpdesk', Icon: Headphones },
  catalog: { label: 'Catalog / Info', Icon: BookOpen },
};

const DEFAULT_PROMPTS = {
  order: `You are a friendly ordering assistant for {biz}. Help customers browse products, answer questions about prices, and take orders. Be concise, accurate with prices, and always confirm before placing an order.`,
  appointment: `You are a booking assistant for {biz}. Help customers schedule appointments, check available services, and manage bookings. Be professional, confirm all details, and make the booking process smooth.`,
  lead: `You are a helpful assistant for {biz}. Understand what customers need, answer their questions, and collect their contact information so the team can follow up. Be friendly and conversational.`,
  support: `You are a customer support assistant for {biz}. Help resolve issues, answer questions, and provide assistance. Be patient, empathetic, and thorough. Escalate to a human agent when needed.`,
  catalog: `You are a helpful information assistant for {biz}. Answer customer questions about products, services, and pricing. Be accurate, concise, and helpful.`,
};

const TABS = [
  { id: 'general', label: 'General', icon: Bot },
  { id: 'prompts', label: 'Prompts', icon: Sparkles },
  { id: 'sheets', label: 'Data Sheets', icon: FileSpreadsheet },
  { id: 'media', label: 'Media', icon: Image },
  { id: 'business', label: 'Business Setup', icon: null },
  { id: 'guide', label: 'How to Use', icon: HelpCircle },
];

export default function AISettingsPage() {
  const { merchant, updateMerchant } = useAuth();
  const [activeTab, setActiveTab] = useState('general');
  const [config, setConfig] = useState({
    systemPrompt: '', fallbackMessage: '', autoReply: false,
  });
  const [saving, setSaving] = useState(false);
  const businessType = merchant?.businessType || 'catalog';
  const btInfo = BUSINESS_TYPES[businessType] || BUSINESS_TYPES.catalog;

  useEffect(() => {
    if (merchant?.aiConfig) setConfig(prev => ({ ...prev, ...merchant.aiConfig }));
  }, [merchant]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await merchantAPI.updateAiConfig({
        systemPrompt: config.systemPrompt,
        fallbackMessage: config.fallbackMessage,
        autoReply: config.autoReply,
      });
      updateMerchant({ aiConfig: { ...merchant.aiConfig, ...config } });
      toast.success('AI settings saved');
    } catch (_) {}
    finally { setSaving(false); }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1 className="page-title">AI Settings</h1>
          <p className="page-subtitle">Configure your AI assistant's behavior</p>
        </div>
        {activeTab === 'general' && (
          <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
            <Save size={15} /> {saving ? 'Saving...' : 'Save Settings'}
          </button>
        )}
      </div>

      <div className="page-body">
        {/* Business Type banner (always visible) */}
        <div className="card" style={{ marginBottom: 20, display: 'flex', alignItems: 'center', gap: 14 }}>
          <div style={{ width: 42, height: 42, borderRadius: 10, background: 'var(--primary-light)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <btInfo.Icon size={20} style={{ color: 'var(--primary)' }} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: '0.9rem' }}>Business Type: {btInfo.label}</div>
            <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Set by your administrator. Contact support to change.</div>
          </div>
        </div>

        {/* Tab Bar */}
        <div style={{ display: 'flex', gap: 4, marginBottom: 24, borderBottom: '2px solid var(--border)', paddingBottom: 0 }}>
          {TABS.map(tab => {
            const Icon = tab.id === 'business' ? btInfo.Icon : tab.icon;
            const isActive = activeTab === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7,
                  padding: '9px 18px', fontSize: '0.85rem', fontWeight: 600,
                  border: 'none', background: 'transparent', cursor: 'pointer',
                  color: isActive ? 'var(--primary)' : 'var(--text-muted)',
                  borderBottom: `2px solid ${isActive ? 'var(--primary)' : 'transparent'}`,
                  marginBottom: -2, borderRadius: '6px 6px 0 0',
                  transition: 'color 0.15s',
                }}
              >
                <Icon size={15} /> {tab.label}
              </button>
            );
          })}
        </div>

        {/* Tab: General */}
        {activeTab === 'general' && (
          <div className="grid-2" style={{ gap: 20, alignItems: 'start' }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div className="card">
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontWeight: 600 }}>
                      <Bot size={16} color="var(--primary)" /> Auto-Reply
                    </div>
                    <p style={{ fontSize: '0.82rem', marginTop: 2 }}>Automatically reply to all incoming messages with AI</p>
                  </div>
                  <button className={`toggle ${config.autoReply ? 'on' : ''}`}
                    onClick={() => setConfig(p => ({ ...p, autoReply: !p.autoReply }))} />
                </div>
                {config.autoReply && (
                  <div className="alert alert-warning" style={{ marginTop: 12, marginBottom: 0 }}>
                    <AlertTriangle size={14} />
                    <span style={{ fontSize: '0.8rem' }}>AI will automatically respond to ALL messages.</span>
                  </div>
                )}
              </div>

              <div className="card">
                <div className="card-title" style={{ marginBottom: 10 }}>Fallback Message</div>
                <textarea className="form-input" rows={2} value={config.fallbackMessage}
                  onChange={e => setConfig(p => ({ ...p, fallbackMessage: e.target.value }))}
                  placeholder="I'm sorry, I'm unable to process your request right now." />
                <div className="form-hint" style={{ marginTop: 6 }}>Sent when AI cannot generate a response</div>
              </div>
            </div>

            <div className="card">
              <div className="flex-between" style={{ marginBottom: 10 }}>
                <div className="card-title">System Prompt</div>
                <button className="btn btn-ghost btn-sm"
                  onClick={() => setConfig(p => ({ ...p, systemPrompt: DEFAULT_PROMPTS[businessType].replace('{biz}', merchant?.businessName || 'your business') }))}>
                  <RotateCcw size={13} /> Reset
                </button>
              </div>
              <textarea className="form-input" rows={12} value={config.systemPrompt}
                onChange={e => setConfig(p => ({ ...p, systemPrompt: e.target.value }))}
                style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', lineHeight: 1.6 }} />
              <div className="form-hint" style={{ marginTop: 6 }}>
                {config.systemPrompt.length} chars · Sheet data + {btInfo.label} instructions are auto-appended
              </div>
            </div>
          </div>
        )}

        {/* Tab: Prompts */}
        {activeTab === 'prompts' && <PromptsSection />}

        {/* Tab: Data Sheets */}
        {activeTab === 'sheets' && <NamedSheetsSection />}

        {/* Tab: Media */}
        {activeTab === 'media' && <MediaTypesSection />}

        {/* Tab: Business Setup */}
        {activeTab === 'business' && (
          <>
            {businessType !== 'catalog' && <DataSheetSelector merchant={merchant} />}
            {businessType === 'order' && <OrderConfigSection merchant={merchant} />}
            {businessType === 'appointment' && <AppointmentConfigSection merchant={merchant} />}
            {businessType === 'lead' && <LeadConfigSection merchant={merchant} />}
            {businessType === 'support' && <SupportConfigSection merchant={merchant} />}
            {businessType === 'catalog' && (
              <div className="card">
                <div className="card-title">Catalog / Information Mode</div>
                <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 8 }}>
                  The AI will answer questions using your connected Google Sheets data. No orders, bookings, or leads will be collected. Connect sheets in the Integrations page and set AI instructions per sheet.
                </p>
              </div>
            )}
          </>
        )}

        {/* Tab: How to Use Guide */}
        {activeTab === 'guide' && <AIUserGuide />}
      </div>
    </div>
  );
}

// ============ Media Types Section ============
function MediaTypesSection() {
  const { merchant } = useAuth();
  const [allowed, setAllowed] = useState([]);
  const [enabled, setEnabled] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    merchantAPI.getMediaTypes().then(({ data }) => {
      setAllowed(data.data.allowedMediaTypes || []);
      setEnabled(data.data.enabledMediaTypes || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const toggle = async (type) => {
    const newEnabled = enabled.includes(type) ? enabled.filter(t => t !== type) : [...enabled, type];
    setEnabled(newEnabled);
    setSaving(true);
    try {
      await merchantAPI.updateMediaTypes({ enabledMediaTypes: newEnabled });
      toast.success('Media settings updated');
    } catch (_) { toast.error('Failed'); }
    finally { setSaving(false); }
  };

  const allTypes = [
    { key: 'image', label: 'Images', desc: 'JPG, PNG, WebP photos', icon: <Image size={16} style={{ color: '#22c55e' }} /> },
    { key: 'video', label: 'Videos', desc: 'MP4, MOV video files', icon: <Film size={16} style={{ color: '#6366f1' }} /> },
    { key: 'audio', label: 'Audio / Voice', desc: 'Voice messages, MP3, OGG', icon: <Mic size={16} style={{ color: '#f59e0b' }} /> },
    { key: 'pdf', label: 'PDF', desc: 'PDF documents', icon: <FileText size={16} style={{ color: '#ef4444' }} /> },
    { key: 'document', label: 'Documents', desc: 'Word files (DOCX, DOC)', icon: <File size={16} style={{ color: '#3b82f6' }} /> },
    { key: 'excel', label: 'Excel', desc: 'Spreadsheets (XLSX, XLS)', icon: <FileSpreadsheet size={16} style={{ color: '#16a34a' }} /> },
    { key: 'csv', label: 'CSV', desc: 'CSV data files', icon: <FileText size={16} style={{ color: '#64748b' }} /> },
  ];

  // Only show types that super admin has allowed
  const availableTypes = allTypes.filter(t => allowed.includes(t.key));

  if (loading) return null;
  if (availableTypes.length === 0) return null;

  return (
    <div style={{ marginTop: 20 }}>
      <div className="card">
        <div className="card-title" style={{ marginBottom: 6 }}>Receive Media Files</div>
        <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: 14 }}>
          Choose which file types your AI can receive and process from customers
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 8 }}>
          {availableTypes.map(type => {
            const isOn = enabled.includes(type.key);
            return (
              <div key={type.key} onClick={() => toggle(type.key)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', borderRadius: 10, cursor: 'pointer',
                  border: `1.5px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`,
                  background: isOn ? 'var(--primary-light)' : 'transparent',
                  transition: 'all 0.15s', opacity: saving ? 0.6 : 1,
                }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', background: isOn ? 'var(--primary-light)' : 'var(--bg)', border: '1px solid var(--border)', flexShrink: 0 }}>
                  {type.icon}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{type.label}</div>
                  <div style={{ fontSize: '0.68rem', color: 'var(--text-muted)' }}>{type.desc}</div>
                </div>
                <div style={{ width: 18, height: 18, borderRadius: 4, border: `2px solid ${isOn ? 'var(--primary)' : 'var(--border)'}`, background: isOn ? 'var(--primary)' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  {isOn && <svg width="10" height="10" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#fff" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============ AI User Guide ============
function GuideSection({ icon: Icon, color, title, children }) {
  const [open, setOpen] = useState(true);
  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div onClick={() => setOpen(p => !p)} style={{ display: 'flex', alignItems: 'center', gap: 12, cursor: 'pointer', userSelect: 'none' }}>
        <div style={{ width: 38, height: 38, borderRadius: 10, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={18} style={{ color }} />
        </div>
        <div style={{ flex: 1, fontWeight: 700, fontSize: '0.92rem' }}>{title}</div>
        {open ? <ChevronDown size={16} style={{ color: 'var(--text-muted)' }} /> : <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />}
      </div>
      {open && <div style={{ marginTop: 14, paddingTop: 14, borderTop: '1px solid var(--border)' }}>{children}</div>}
    </div>
  );
}

function GuideStep({ num, title, children }) {
  return (
    <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
      <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.75rem', fontWeight: 800, flexShrink: 0, marginTop: 1 }}>{num}</div>
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4 }}>{title}</div>
        <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>{children}</div>
      </div>
    </div>
  );
}

function GuideTip({ children }) {
  return (
    <div style={{ display: 'flex', gap: 8, padding: '10px 12px', background: 'var(--primary-light)', borderRadius: 8, marginBottom: 8, fontSize: '0.8rem', lineHeight: 1.5 }}>
      <CheckCircle size={14} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
      <span>{children}</span>
    </div>
  );
}

function GuideCode({ children }) {
  return (
    <div style={{ background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 14px', fontFamily: 'var(--font-mono)', fontSize: '0.78rem', lineHeight: 1.7, marginBottom: 10, whiteSpace: 'pre-wrap' }}>
      {children}
    </div>
  );
}

function AIUserGuide() {
  return (
    <div style={{ maxWidth: 720 }}>
      {/* Intro banner */}
      <div style={{ padding: '18px 20px', background: 'var(--primary-light)', border: '1px solid var(--primary)', borderRadius: 12, marginBottom: 20, display: 'flex', gap: 14, alignItems: 'flex-start' }}>
        <HelpCircle size={22} style={{ color: 'var(--primary)', flexShrink: 0, marginTop: 2 }} />
        <div>
          <div style={{ fontWeight: 700, fontSize: '0.95rem', marginBottom: 4 }}>How the AI Assistant Works</div>
          <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
            Your AI assistant automatically replies to customer messages on WhatsApp, Instagram, Telegram, and other channels. This guide walks you through everything you need to set it up correctly.
          </div>
        </div>
      </div>

      {/* Section 1: Quick Start */}
      <GuideSection icon={Zap} color="#f59e0b" title="Step 1 — Quick Start Checklist">
        <GuideTip>Go to <strong>General tab</strong> → turn on <strong>Auto-Reply</strong> → click Save. The AI will now reply to all incoming messages automatically.</GuideTip>
        <GuideTip>Go to <strong>Business Setup tab</strong> → configure your services, hours, and confirmation messages.</GuideTip>
        <GuideTip>Go to <strong>Integrations</strong> → connect Google Sheets or upload files so the AI has data to answer from.</GuideTip>
        <GuideTip>Go to <strong>Prompts tab</strong> → create a custom prompt that tells the AI exactly how to behave for your business.</GuideTip>
        <GuideTip>Assign the prompt to a specific channel from the Prompts tab → Channel → Prompt Assignments section.</GuideTip>
      </GuideSection>

      {/* Section 2: General Tab */}
      <GuideSection icon={Bot} color="#6366f1" title="General Tab — Core AI Behaviour">
        <GuideStep num="1" title="Auto-Reply">
          When turned ON, the AI automatically replies to every incoming customer message. When OFF, messages arrive in your Inbox for you to reply manually.
        </GuideStep>
        <GuideStep num="2" title="System Prompt">
          This is the main instruction for the AI — it tells the AI who it is, what it does, and how to behave. Think of it as the AI's job description.
          <GuideCode>{`You are a booking assistant for My Business.
Help customers schedule appointments and answer
questions about our services. Be professional
and friendly.`}</GuideCode>
          If you leave this blank, the AI uses a built-in default for your business type.
        </GuideStep>
        <GuideStep num="3" title="Fallback Message">
          This message is sent when the AI cannot generate a reply (e.g. no internet, API error). Set it to something like: <em>"Our team will get back to you shortly."</em>
        </GuideStep>
      </GuideSection>

      {/* Section 3: Prompts */}
      <GuideSection icon={Sparkles} color="#8b5cf6" title="Prompts Tab — Multiple AI Personalities">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Custom Prompts let you create different AI behaviours for different channels. For example: one prompt for your WhatsApp sales channel, another for your Instagram support channel.
        </p>
        <GuideStep num="1" title="Create a Prompt">
          Click <strong>New Prompt</strong> → pick a template or write your own → give it a name → click Create.
        </GuideStep>
        <GuideStep num="2" title="Write a Good Prompt">
          A good prompt tells the AI: what topics to answer, which documents to use for which question, and how to collect information.
          <GuideCode>{`You are an assistant for ABC Company.

For JOB questions → use the "Job Openings" document.
For FRANCHISE questions → use the "Franchise Info" document.
For BOOKING → share the booking link when asked.

When collecting info, ask for:
name, phone number, email, and their interest.`}</GuideCode>
        </GuideStep>
        <GuideStep num="3" title="Attach Data Sources">
          When creating/editing a prompt, scroll to <strong>Data Sources</strong>. Check which uploaded files and connected sheets this prompt should use. Leave all unchecked to use everything.
        </GuideStep>
        <GuideStep num="4" title="Save Collected Data to Google Sheet">
          In <strong>Data Sources → Save Collected Data</strong>, select a Google Spreadsheet and sheet tab name. When the AI collects customer info during the conversation, it will automatically save it to that sheet.
        </GuideStep>
        <GuideStep num="5" title="Assign Prompt to a Channel">
          Scroll to <strong>Channel → Prompt Assignments</strong> at the bottom of the Prompts tab. Select which prompt each channel should use. Channels not assigned will use your global System Prompt.
        </GuideStep>
        <GuideTip>If a channel has a prompt assigned, it will follow ONLY that prompt — the global system prompt is ignored for that channel.</GuideTip>
      </GuideSection>

      {/* Section 4: Data Sources */}
      <GuideSection icon={Database} color="#22c55e" title="Connecting Data — Sheets & Files">
        <GuideStep num="1" title="Upload a Document (PDF, Word, Excel)">
          Go to <strong>Integrations → Knowledge Base</strong> → click Upload → select your file. The AI will read its contents and use it to answer customer questions.
          <br /><br />
          <strong>Example uses:</strong> Price list, service menu, FAQ document, franchise info PDF, job openings sheet, company policy.
        </GuideStep>
        <GuideStep num="2" title="Connect a Google Sheet">
          Go to <strong>Integrations → Google Sheets</strong> → connect your Google account → select a spreadsheet and tab → give it a label (e.g. "Price List", "Inventory"). The AI reads this data in real time to answer price and product questions.
        </GuideStep>
        <GuideStep num="3" title="Tell the AI Which File to Use for Which Topic">
          In your prompt, reference the file or sheet by its name/purpose:
          <GuideCode>{`For questions about job openings → use the "Career" document.
For franchise info → use the "Franchise" document.
For product prices → use the "Price List" sheet.`}</GuideCode>
          Then in the prompt's <strong>Data Sources</strong> section, check only the relevant files for that prompt.
        </GuideStep>
        <GuideTip>The AI only sees data from files and sheets you've checked for that prompt. This prevents mixing up career info with price info.</GuideTip>
      </GuideSection>

      {/* Section 5: Named Sheet Destinations */}
      <GuideSection icon={FileSpreadsheet} color="#16a34a" title="Data Sheets Tab — Save Different Data to Different Sheets">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Named Sheet Destinations let you configure <strong>multiple Google Sheets</strong>, each for a different type of request.
          The AI automatically decides which sheet to save to based on what the customer is asking about — no coding required.
        </p>

        <GuideStep num="1" title="When to use this">
          Use this when your business handles <strong>more than one type of inquiry</strong> and you want each saved separately. Examples:
          <div style={{ marginTop: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
            {[
              { label: 'Job Application', desc: 'Customer applies for a job → save to "Job Applications" sheet' },
              { label: 'Finance Enquiry', desc: 'Customer asks about a loan or financing → save to "Finance Leads" sheet' },
              { label: 'Franchise Request', desc: 'Customer wants to open a franchise → save to "Franchise Enquiries" sheet' },
              { label: 'Product Order', desc: 'Customer orders a product → save to "Orders" sheet' },
              { label: 'General Contact', desc: 'Customer just wants a callback → save to "General Leads" sheet' },
            ].map(ex => (
              <div key={ex.label} style={{ display: 'flex', gap: 8, padding: '7px 10px', background: 'var(--bg)', borderRadius: 7, border: '1px solid var(--border)', fontSize: '0.78rem' }}>
                <span style={{ fontWeight: 700, color: '#16a34a', whiteSpace: 'nowrap' }}>{ex.label}:</span>
                <span style={{ color: 'var(--text-muted)' }}>{ex.desc}</span>
              </div>
            ))}
          </div>
        </GuideStep>

        <GuideStep num="2" title="Set up your sheet destinations">
          Go to <strong>Data Sheets tab</strong> → click <strong>Add Sheet</strong> → fill in:
          <ul style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.9, marginTop: 8, paddingLeft: 18 }}>
            <li><strong>Destination Name</strong> — a clear label, e.g. <em>Job Applications</em></li>
            <li><strong>Google Spreadsheet</strong> — pick from your connected sheets (or paste the ID)</li>
            <li><strong>Sheet Tab Name</strong> — the tab inside that spreadsheet, e.g. <em>Applications</em></li>
            <li><strong>Trigger Description</strong> — when should the AI use this? e.g. <em>When user applies for a job position</em></li>
            <li><strong>Fields to Collect</strong> — what info to ask for, e.g. <em>name, email, phone, position applied, years of experience</em></li>
          </ul>
        </GuideStep>

        <GuideStep num="3" title="Copy the auto-generated prompt snippet">
          After adding your sheets, the <strong>Data Sheets tab</strong> shows a ready-made prompt snippet. Click <strong>Copy</strong> and paste it into your AI prompt (in the Prompts tab).
          <GuideCode>{`If related to job application, collect name, email, phone,
position applied, experience and save to "Job Applications" sheet.

If related to finance enquiry, collect name, email, phone,
loan amount, purpose and save to "Finance Leads" sheet.`}</GuideCode>
        </GuideStep>

        <GuideStep num="4" title="How the AI saves the data">
          When the AI has collected all required fields for a matching scenario, it will:
          <ol style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.9, marginTop: 8, paddingLeft: 18 }}>
            <li>Automatically identify which sheet destination matches the conversation</li>
            <li>Collect all the fields you specified before saving</li>
            <li>Append a new row to the correct Google Sheet in real time</li>
            <li>Continue the conversation normally — the customer never sees this happening</li>
          </ol>
        </GuideStep>

        <GuideStep num="5" title="Full example — Multi-request business">
          A recruitment company handles jobs, finance referrals, and general enquiries all through one WhatsApp number. Their prompt looks like this:
          <GuideCode>{`You are an assistant for XYZ Recruitment.

For JOB APPLICATIONS:
- Ask: full name, email, phone, position, years of experience
- Save to "Job Applications" sheet

For BUSINESS FINANCING enquiries:
- Ask: name, phone, company name, loan amount needed, purpose
- Save to "Finance Leads" sheet

For GENERAL CONTACT:
- Ask: name, phone, reason for contact
- Save to "General Leads" sheet

For any other question, answer using the knowledge base provided.
Be professional, friendly, and concise.`}</GuideCode>
        </GuideStep>

        <GuideTip>You can have as many sheet destinations as you need. Each one is completely independent — different spreadsheet, different tab, different fields.</GuideTip>
        <GuideTip>Make sure the sheet tab name you enter matches exactly (case-sensitive) with the tab inside your Google Spreadsheet.</GuideTip>
        <GuideTip>The AI will only save once it has collected ALL the fields you listed. It will keep asking follow-up questions until it has everything.</GuideTip>
      </GuideSection>

      {/* Section 6: Business Types */}
      <GuideSection icon={Settings} color="#3b82f6" title="Business Setup Tab — What the AI Can Do">
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14, lineHeight: 1.6 }}>
          Your business type controls what the AI does with collected information. Each type has a different flow.
        </p>
        {[
          { type: 'Appointment / Booking', color: '#6366f1', desc: 'AI helps customers book appointments, asks for service + date + time, and saves bookings to your system. Enable the Public Booking Page to share a link where customers can self-book.', example: '"I want to book a haircut for Friday at 3pm"' },
          { type: 'Order / E-Commerce', color: '#f59e0b', desc: 'AI takes product orders from the menu/catalog, calculates totals, and saves orders to Google Sheets. You can configure a payment link to be sent after each order.', example: '"I\'d like to order 2 chicken burgers and a Coke"' },
          { type: 'Lead / Inquiry', color: '#22c55e', desc: 'AI collects customer contact info (name, phone, email, interest) and saves them as leads. Good for real estate, consulting, or any business where someone needs to follow up.', example: '"I\'m interested in your services, how does it work?"' },
          { type: 'Support / Helpdesk', color: '#ef4444', desc: 'AI tries to resolve customer issues. If it can\'t, it creates a support ticket and notifies your team. Good for product support or after-sales service.', example: '"My order hasn\'t arrived yet, order #1234"' },
          { type: 'Catalog / Info', color: '#64748b', desc: 'AI answers questions using your connected sheets and files. No orders, bookings, or leads are collected. Good for information-only businesses.', example: '"What is the price of Product X?"' },
        ].map(b => (
          <div key={b.type} style={{ marginBottom: 12, padding: '12px 14px', background: 'var(--bg)', borderRadius: 10, borderLeft: `3px solid ${b.color}` }}>
            <div style={{ fontWeight: 700, fontSize: '0.85rem', marginBottom: 4, color: b.color }}>{b.type}</div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6, marginBottom: 6 }}>{b.desc}</div>
            <div style={{ fontSize: '0.75rem', fontStyle: 'italic', color: 'var(--text-muted)' }}>Customer example: {b.example}</div>
          </div>
        ))}
      </GuideSection>

      {/* Section 6: Inbox & CRM */}
      <GuideSection icon={MessageSquare} color="#ec4899" title="Inbox & CRM — Managing Conversations">
        <GuideStep num="1" title="Inbox">
          All customer messages appear in your <strong>Inbox</strong>. When Auto-Reply is ON, the AI handles them automatically. You can jump in at any time by typing a reply — your message goes directly to the customer.
        </GuideStep>
        <GuideStep num="2" title="Hand to Agent">
          In any conversation, click <strong>Hand to Agent</strong> to pause AI auto-replies for that customer. The AI will stop replying until you click <strong>Resume AI</strong>. Useful when a conversation needs human attention.
        </GuideStep>
        <GuideStep num="3" title="CRM — Track Deals">
          Go to <strong>CRM</strong> in the sidebar. Every time the AI collects a lead from a conversation, a deal is automatically created in your CRM pipeline.
          You can also manually create deals, move them through stages (New → Contacted → Qualified → Won), add notes, and log activities like calls and meetings.
        </GuideStep>
        <GuideStep num="4" title="Bookings — View Collected Data">
          Go to <strong>Bookings</strong> to see all appointments, orders, leads, and support tickets the AI has collected. You can update their status, push leads to CRM, and view customer details.
        </GuideStep>
      </GuideSection>

      {/* Section 7: Tips */}
      <GuideSection icon={CheckCircle} color="#22c55e" title="Tips for Best Results">
        <GuideTip>Be specific in your prompt. Instead of "answer questions", write "for price questions use the Price List sheet, for job questions use the Career document".</GuideTip>
        <GuideTip>Test your bot by messaging your own WhatsApp/Instagram channel after setup.</GuideTip>
        <GuideTip>If the AI gives wrong answers, check that the correct files/sheets are attached to the prompt in Data Sources.</GuideTip>
        <GuideTip>Use the Booking Page for appointment businesses — customers can self-book 24/7 without messaging.</GuideTip>
        <GuideTip>Create separate prompts for different purposes (sales, support, careers) and assign each to the right channel.</GuideTip>
        <GuideTip>Keep prompts clear and under 500 words. Shorter prompts are faster and more accurate.</GuideTip>
        <GuideTip>Set a Fallback Message in General tab so customers always get a response even if the AI fails.</GuideTip>
      </GuideSection>
    </div>
  );
}

/* --- ORDER CONFIG --- */
// ============ Prompts Section ============
const EMPTY_FORM = { name: '', prompt: '', dataSources: { knowledgeFileIds: [], sheetIds: [], saveToSpreadsheetId: '', saveToSheetName: 'Leads' } };

const PROMPT_TEMPLATES = [
  {
    category: 'Booking & Appointments',
    items: [
      {
        name: 'Booking Assistant',
        prompt: `You are a professional booking assistant for {businessName}. Help customers schedule appointments for our services.

When a customer wants to book:
1. Ask what service they need
2. Ask their preferred date and time
3. Collect their name and contact number
4. Confirm all details before finalizing

If they ask about services, pricing, or availability — answer using the data provided.
If they ask for the booking link — share it.

Always be warm, professional, and concise.`,
      },
      {
        name: 'Salon & Beauty',
        prompt: `You are a friendly booking assistant for {businessName}, a beauty salon.

Help customers book appointments for haircuts, coloring, styling, facials, or other services.

When booking, collect:
- Their name
- Service they want
- Preferred stylist (if any)
- Preferred date and time
- Contact number

For color or treatment services, mention it may take 2–3 hours.
If they ask about prices or services — answer from the data provided.
Be warm, casual, and welcoming.`,
      },
      {
        name: 'Clinic / Doctor',
        prompt: `You are a receptionist assistant for {businessName}. Help patients book appointments and answer questions about services.

When booking, collect:
- Patient name
- Reason for visit or service needed
- Preferred doctor (if applicable)
- Preferred date and time
- Contact number

Never give medical advice or diagnoses. For emergencies, tell them to call emergency services immediately.
For questions about doctors, treatments, or services — refer to the knowledge base provided.
Be professional, calm, and caring.`,
      },
    ],
  },
  {
    category: 'Sales & Leads',
    items: [
      {
        name: 'Lead Collection',
        prompt: `You are a helpful sales assistant for {businessName}. Your goal is to understand what the customer needs, answer their questions, and collect their contact details for follow-up.

When you have answered their questions, say:
"To connect you with the right person on our team, could I get your name and best contact number?"

Collect: name, phone number, email (optional), and what they're interested in.
Answer questions using the data and documents provided.
Be friendly, professional, and never pushy.`,
      },
      {
        name: 'Real Estate Inquiry',
        prompt: `You are a property inquiry assistant for {businessName}. Help potential buyers or renters find the right property.

When someone inquires, collect:
- Are they buying or renting?
- Property type: apartment, house, commercial?
- Budget range
- Preferred location or area
- Number of bedrooms
- Timeline — when do they need to move?
- Name and contact number

Once collected, say: "Thank you! One of our agents will contact you within 24 hours."
Answer property questions using the data provided.`,
      },
      {
        name: 'Franchise Inquiry',
        prompt: `You are a business development assistant for {businessName}. Help interested parties learn about franchise or partnership opportunities.

When someone is interested, collect:
- Their full name
- City/location they want to open in
- Contact number and email
- Brief background — have they run a business before?

For questions about franchise costs, requirements, or process — refer to the franchise document provided.
Do not make promises or commitments. Say: "Our franchise team will review your details and contact you within 2–3 business days."`,
      },
    ],
  },
  {
    category: 'Jobs & Careers',
    items: [
      {
        name: 'Career / Hiring Bot',
        prompt: `You are a hiring assistant for {businessName}. Help job seekers learn about open positions and apply.

For questions about open roles, requirements, or salary — refer to the career/jobs document provided.

When a candidate is interested in a role, collect:
- Full name
- Position they want to apply for
- Contact number and email
- Years of relevant experience
- Current location

Say: "Thank you for your interest! Our HR team will contact you within 3 business days."
Do not make salary promises beyond what is listed in the documents.`,
      },
    ],
  },
  {
    category: 'Orders & E-Commerce',
    items: [
      {
        name: 'Order Taking Bot',
        prompt: `You are an ordering assistant for {businessName}. Help customers browse the menu/catalog, answer questions, and place orders.

When taking an order:
1. Help them select items using the price list or menu data provided
2. Confirm each item, quantity, and any customizations
3. Ask: delivery or pickup?
4. For delivery: collect full address
5. Confirm total and estimated time

Never guess prices — only use what is in the data provided.
If an item is not in the data, say it is not available.`,
      },
      {
        name: 'Restaurant / Food',
        prompt: `You are a friendly order assistant for {businessName}. Help customers place food orders for delivery or pickup.

Use the menu data to answer all price and availability questions accurately.

Order flow:
1. Greet and ask what they'd like to order
2. Take their order item by item, confirm quantities
3. Ask about customizations (spice level, extras, allergies)
4. Ask: delivery or pickup?
5. Collect address for delivery
6. Confirm order summary and total

Be friendly and fast. If something is not on the menu, say it's not available today.`,
      },
    ],
  },
  {
    category: 'Support & Helpdesk',
    items: [
      {
        name: 'Customer Support',
        prompt: `You are a customer support assistant for {businessName}. Help customers resolve issues, answer questions, and provide assistance.

Support flow:
1. Understand the customer's issue clearly
2. Try to resolve it using the information available to you
3. If you cannot resolve it, collect their name and contact number and say: "Our support team will follow up within 24 hours."

Be patient, empathetic, and professional. Never argue with the customer.
For product or policy questions — use the knowledge base provided.`,
      },
    ],
  },
  {
    category: 'Multi-Purpose',
    items: [
      {
        name: 'General Assistant',
        prompt: `You are a helpful assistant for {businessName}. Answer customer questions, provide information, and guide them to the right service.

For questions about services, prices, or products — use the data and documents provided.
For booking an appointment — share the booking link when requested.
For career inquiries — refer to the jobs/career document.
For franchise inquiries — refer to the franchise document.
For anything else — answer helpfully and professionally.

If you don't have the answer, say: "Let me connect you with our team — could I get your name and contact number?"`,
      },
    ],
  },
];

function PromptsSection() {
  const { merchant } = useAuth();
  const [prompts, setPrompts] = useState([]);
  const [channels, setChannels] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [assignChannel, setAssignChannel] = useState(null);
  const [knowledgeFiles, setKnowledgeFiles] = useState([]);
  const [connectedSheets, setConnectedSheets] = useState([]);
  const [spreadsheets, setSpreadsheets] = useState([]);

  useEffect(() => {
    merchantAPI.getPrompts().then(({ data }) => setPrompts(data.data || [])).catch(() => {});
    channelsAPI.list().then(({ data }) => setChannels(data.data || [])).catch(() => {});
    integrationsAPI.getFiles().then(({ data }) => setKnowledgeFiles((data.data || []).filter(f => f.status === 'ready'))).catch(() => {});
    integrationsAPI.getConnectedSheets().then(({ data }) => setConnectedSheets((data.data || []).filter(s => s.isActive))).catch(() => {});
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, []);

  const openNew = () => { setForm(EMPTY_FORM); setEditId(null); setShowAdd(true); };
  const openEdit = (p) => {
    setForm({
      name: p.name,
      prompt: p.prompt,
      dataSources: {
        knowledgeFileIds: (p.dataSources?.knowledgeFileIds || []).map(id => id.toString()),
        sheetIds: p.dataSources?.sheetIds || [],
        saveToSpreadsheetId: p.dataSources?.saveToSpreadsheetId || '',
        saveToSheetName: p.dataSources?.saveToSheetName || 'Leads',
      },
    });
    setEditId(p._id);
    setShowAdd(true);
  };

  const toggleFileId = (id) => {
    setForm(f => {
      const ids = f.dataSources.knowledgeFileIds;
      return { ...f, dataSources: { ...f.dataSources, knowledgeFileIds: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] } };
    });
  };
  const toggleSheetId = (id) => {
    setForm(f => {
      const ids = f.dataSources.sheetIds;
      return { ...f, dataSources: { ...f.dataSources, sheetIds: ids.includes(id) ? ids.filter(x => x !== id) : [...ids, id] } };
    });
  };

  const handleSave = async () => {
    if (!form.name.trim() || !form.prompt.trim()) return toast.error('Name and prompt are required');
    try {
      const payload = { name: form.name, prompt: form.prompt, dataSources: form.dataSources };
      if (editId) {
        const { data } = await merchantAPI.updatePrompt(editId, payload);
        setPrompts(data.data);
      } else {
        const { data } = await merchantAPI.createPrompt(payload);
        setPrompts(data.data);
      }
      setShowAdd(false); setEditId(null); setForm(EMPTY_FORM);
      toast.success(editId ? 'Prompt updated' : 'Prompt created');
    } catch (_) {}
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this prompt?')) return;
    try {
      const { data } = await merchantAPI.deletePrompt(id);
      setPrompts(data.data);
      toast.success('Prompt deleted');
    } catch (_) {}
  };

  const handleSetDefault = async (id) => {
    try {
      const { data } = await merchantAPI.updatePrompt(id, { isDefault: true });
      setPrompts(data.data);
      toast.success('Default prompt set');
    } catch (_) {}
  };

  const handleAssign = async (channelId, promptId) => {
    try {
      await merchantAPI.assignChannelPrompt(channelId, { promptId });
      setChannels(prev => prev.map(c => c._id === channelId ? { ...c, customAiConfig: { ...c.customAiConfig, promptId, overrideGlobal: !!promptId } } : c));
      toast.success('Prompt assigned to channel');
      setAssignChannel(null);
    } catch (_) {}
  };

  const getChannelPromptName = (ch) => {
    const pid = ch.customAiConfig?.promptId;
    if (!pid) return null;
    const p = prompts.find(pr => pr._id === pid);
    return p?.name || null;
  };

  return (
    <div style={{ marginTop: 20 }}>
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <div>
            <div className="card-title">Custom Prompts</div>
            <p style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Create different AI prompts and assign them to specific channels</p>
          </div>
          {(() => {
            const maxP = merchant?.limits?.maxPrompts ?? 1;
            const atLimit = maxP !== -1 && prompts.length >= maxP;
            return atLimit ? (
              <button className="btn btn-secondary btn-sm" disabled style={{ opacity: 0.5 }}>Limit ({maxP}) — Upgrade</button>
            ) : (
              <button className="btn btn-primary btn-sm" onClick={openNew}>
                <Plus size={13} /> New Prompt
              </button>
            );
          })()}
        </div>

        {prompts.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text-muted)', fontSize: '0.82rem' }}>
            No custom prompts yet. The global system prompt above is used for all channels.
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {prompts.map(p => (
              <div key={p._id} style={{ padding: '12px 14px', background: 'var(--bg)', borderRadius: 10, border: `1.5px solid ${p.isDefault ? 'var(--primary)' : 'var(--border)'}` }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>{p.name}</span>
                      {p.isDefault && <span className="badge badge-mauve" style={{ fontSize: '0.65rem' }}>Default</span>}
                      {p.dataSources?.knowledgeFileIds?.length > 0 && <span className="badge badge-blue" style={{ fontSize: '0.65rem' }}>{p.dataSources.knowledgeFileIds.length} file{p.dataSources.knowledgeFileIds.length > 1 ? 's' : ''}</span>}
                      {p.dataSources?.sheetIds?.length > 0 && <span className="badge badge-green" style={{ fontSize: '0.65rem' }}>{p.dataSources.sheetIds.length} sheet{p.dataSources.sheetIds.length > 1 ? 's' : ''}</span>}
                      {p.dataSources?.saveToSpreadsheetId && <span className="badge badge-yellow" style={{ fontSize: '0.65rem' }}>saves data</span>}
                    </div>
                    <div style={{ fontSize: '0.76rem', color: 'var(--text-muted)', marginTop: 4, lineHeight: 1.5, maxHeight: 40, overflow: 'hidden' }}>
                      {p.prompt.slice(0, 120)}{p.prompt.length > 120 ? '...' : ''}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: 4, flexShrink: 0 }}>
                    {!p.isDefault && (
                      <button className="btn btn-ghost btn-sm" title="Set as default" onClick={() => handleSetDefault(p._id)}><Star size={13} /></button>
                    )}
                    <button className="btn btn-ghost btn-sm" title="Assign to channel" onClick={() => setAssignChannel(p)}><Link2 size={13} /></button>
                    <button className="btn btn-ghost btn-sm" title="Edit" onClick={() => openEdit(p)}><Edit3 size={13} /></button>
                    <button className="btn btn-ghost btn-sm" title="Delete" onClick={() => handleDelete(p._id)}><Trash2 size={13} /></button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Connected channels & their assigned prompts */}
        {channels.length > 0 && prompts.length > 0 && (
          <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid var(--border)' }}>
            <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Channel → Prompt Assignments</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {channels.filter(c => c.status === 'connected').map(ch => (
                <div key={ch._id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--bg)', borderRadius: 8 }}>
                  <div style={{ flex: 1, fontSize: '0.82rem' }}>
                    <span style={{ fontWeight: 600 }}>{ch.name}</span>
                    <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>({ch.type})</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <select className="form-input" style={{ fontSize: '0.78rem', padding: '4px 8px', width: 'auto', minWidth: 150 }}
                      value={ch.customAiConfig?.promptId || ''}
                      onChange={e => handleAssign(ch._id, e.target.value || null)}>
                      <option value="">Global Prompt</option>
                      {prompts.map(p => <option key={p._id} value={p._id}>{p.name}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Add/Edit Prompt Modal */}
      {showAdd && (
        <div className="modal-overlay" onClick={() => { setShowAdd(false); setEditId(null); setForm(EMPTY_FORM); }}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <span className="modal-title">{editId ? 'Edit Prompt' : 'New Prompt'}</span>
              <button className="modal-close" onClick={() => { setShowAdd(false); setEditId(null); setForm(EMPTY_FORM); }}><X size={18} /></button>
            </div>

            {/* Template picker — only show when creating new */}
            {!editId && !form.prompt && (
              <TemplatePicker onSelect={(tpl) => setForm(f => ({ ...f, name: tpl.name, prompt: tpl.prompt }))} />
            )}
            {!editId && form.prompt && (
              <button className="btn btn-ghost btn-sm" style={{ marginBottom: 12, fontSize: '0.78rem' }}
                onClick={() => setForm(f => ({ ...f, name: '', prompt: '' }))}>
                ← Change template
              </button>
            )}

            <div className="form-group">
              <label className="form-label">Prompt Name</label>
              <input className="form-input" placeholder="e.g. Sales Bot, Support Agent, FAQ Bot" value={form.name}
                onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">System Prompt</label>
              <textarea className="form-input" rows={7} value={form.prompt}
                onChange={e => setForm(p => ({ ...p, prompt: e.target.value }))}
                placeholder="You are a helpful assistant for..."
                style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', lineHeight: 1.6 }} />
              <div className="form-hint" style={{ marginTop: 4 }}>{form.prompt.length} characters</div>
            </div>

            {/* Data Sources */}
            {(knowledgeFiles.length > 0 || connectedSheets.length > 0) && (
              <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 4 }}>
                <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Data Sources <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.78rem' }}>(limit to specific files/sheets for this prompt)</span></div>

                {knowledgeFiles.length > 0 && (
                  <div className="form-group">
                    <label className="form-label" style={{ marginBottom: 6 }}>Knowledge Files</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 140, overflowY: 'auto', padding: '2px 0' }}>
                      {knowledgeFiles.map(f => {
                        const checked = form.dataSources.knowledgeFileIds.includes(f._id);
                        return (
                          <label key={f._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', padding: '4px 6px', borderRadius: 6, background: checked ? 'var(--primary-light)' : 'transparent' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleFileId(f._id)} style={{ accentColor: 'var(--primary)' }} />
                            <span style={{ fontWeight: 500 }}>{f.originalName || f.name}</span>
                            {f.purpose && <span style={{ color: 'var(--text-muted)', fontSize: '0.73rem' }}>· {f.purpose}</span>}
                          </label>
                        );
                      })}
                    </div>
                    <div className="form-hint">Leave all unchecked to use all files</div>
                  </div>
                )}

                {connectedSheets.length > 0 && (
                  <div className="form-group">
                    <label className="form-label" style={{ marginBottom: 6 }}>Connected Sheets</label>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 5, maxHeight: 120, overflowY: 'auto', padding: '2px 0' }}>
                      {connectedSheets.map(s => {
                        const checked = form.dataSources.sheetIds.includes(s._id);
                        return (
                          <label key={s._id} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: '0.82rem', padding: '4px 6px', borderRadius: 6, background: checked ? 'var(--primary-light)' : 'transparent' }}>
                            <input type="checkbox" checked={checked} onChange={() => toggleSheetId(s._id)} style={{ accentColor: 'var(--primary)' }} />
                            <span style={{ fontWeight: 500 }}>{s.spreadsheetName || s.sheetName}</span>
                            {s.purposeLabel && <span style={{ color: 'var(--text-muted)', fontSize: '0.73rem' }}>· {s.purposeLabel}</span>}
                          </label>
                        );
                      })}
                    </div>
                    <div className="form-hint">Leave all unchecked to use all sheets</div>
                  </div>
                )}
              </div>
            )}

            {/* Save collected data to */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 14, marginBottom: 4 }}>
              <div style={{ fontWeight: 600, fontSize: '0.85rem', marginBottom: 10 }}>Save Collected Data <span style={{ fontWeight: 400, color: 'var(--text-muted)', fontSize: '0.78rem' }}>(optional — AI collects user info and saves to this sheet)</span></div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'end' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Google Spreadsheet</label>
                  <select className="form-input" value={form.dataSources.saveToSpreadsheetId}
                    onChange={e => setForm(f => ({ ...f, dataSources: { ...f.dataSources, saveToSpreadsheetId: e.target.value } }))}>
                    <option value="">None — don't save</option>
                    {spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Sheet Tab</label>
                  <input className="form-input" placeholder="Leads" value={form.dataSources.saveToSheetName}
                    onChange={e => setForm(f => ({ ...f, dataSources: { ...f.dataSources, saveToSheetName: e.target.value } }))}
                    style={{ width: 100 }} disabled={!form.dataSources.saveToSpreadsheetId} />
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={() => { setShowAdd(false); setEditId(null); setForm(EMPTY_FORM); }}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave}><Save size={13} /> {editId ? 'Update' : 'Create'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign to Channel Modal */}
      {assignChannel && (
        <div className="modal-overlay" onClick={() => setAssignChannel(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <span className="modal-title">Assign "{assignChannel.name}" to Channel</span>
              <button className="modal-close" onClick={() => setAssignChannel(null)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginBottom: 14 }}>
              Select which channel should use this prompt instead of the global one.
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {channels.filter(c => c.status === 'connected').map(ch => {
                const isAssigned = ch.customAiConfig?.promptId === assignChannel._id;
                return (
                  <div key={ch._id} onClick={() => handleAssign(ch._id, isAssigned ? null : assignChannel._id)}
                    style={{
                      padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
                      border: `2px solid ${isAssigned ? 'var(--primary)' : 'var(--border)'}`,
                      background: isAssigned ? 'var(--primary-light)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    }}>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>{ch.name}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'capitalize' }}>{ch.type}</div>
                    </div>
                    {isAssigned && <span className="badge badge-green">Assigned</span>}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TemplatePicker({ onSelect }) {
  const [openCat, setOpenCat] = useState(null);

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8, color: 'var(--text-muted)' }}>
        Start from a template — or scroll down to write your own
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 280, overflowY: 'auto' }}>
        {PROMPT_TEMPLATES.map(cat => (
          <div key={cat.category}>
            <button onClick={() => setOpenCat(openCat === cat.category ? null : cat.category)}
              style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer', fontWeight: 700, fontSize: '0.8rem' }}>
              <span>{cat.category}</span>
              <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{openCat === cat.category ? '▲' : '▼'} {cat.items.length} template{cat.items.length > 1 ? 's' : ''}</span>
            </button>
            {openCat === cat.category && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 4, paddingLeft: 8 }}>
                {cat.items.map(tpl => (
                  <div key={tpl.name} onClick={() => onSelect(tpl)}
                    style={{ padding: '8px 12px', borderRadius: 8, border: '1px solid var(--border)', cursor: 'pointer', background: 'var(--white)' }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--primary)'; e.currentTarget.style.background = 'var(--primary-light)'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'var(--white)'; }}>
                    <div style={{ fontWeight: 600, fontSize: '0.82rem' }}>{tpl.name}</div>
                    <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {tpl.prompt.slice(0, 80)}...
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
      <div style={{ borderTop: '1px solid var(--border)', marginTop: 10, paddingTop: 10, textAlign: 'center', fontSize: '0.78rem', color: 'var(--text-muted)' }}>
        or write your own prompt below ↓
      </div>
    </div>
  );
}

function OrderConfigSection({ merchant }) {
  const [config, setConfig] = useState({ enabled: false, orderSheetSpreadsheetId: '', orderSheetName: 'Orders', paymentLink: '', paymentInstructions: '', confirmationMessage: 'Your order has been placed! Thank you.', currency: 'MYR' });
  const [saving, setSaving] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);

  useEffect(() => {
    if (merchant?.orderConfig) setConfig(p => ({ ...p, ...merchant.orderConfig }));
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, [merchant]);

  const save = async () => { setSaving(true); try { await merchantAPI.updateOrderConfig(config); toast.success('Saved'); } catch (_) {} finally { setSaving(false); } };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div><div className="card-title">Order Taking Settings</div><p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>AI takes orders, saves to Google Sheet, sends payment link</p></div>
        <button className={`toggle ${config.enabled ? 'on' : ''}`} onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))} />
      </div>
      {config.enabled && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
          <div className="form-group"><label className="form-label">Save Orders To</label><select className="form-input" value={config.orderSheetSpreadsheetId} onChange={e => setConfig(p => ({ ...p, orderSheetSpreadsheetId: e.target.value }))}><option value="">Select spreadsheet...</option>{spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Sheet Tab</label><input className="form-input" value={config.orderSheetName} onChange={e => setConfig(p => ({ ...p, orderSheetName: e.target.value }))} placeholder="Orders" /></div>
          <div className="form-group"><label className="form-label">Currency</label><select className="form-input" value={config.currency} onChange={e => setConfig(p => ({ ...p, currency: e.target.value }))}>{['USD','EUR','GBP','MYR','INR','SGD','AED','SAR','AUD','CAD'].map(c => <option key={c}>{c}</option>)}</select></div>
          <div className="form-group"><label className="form-label">Payment Link</label><input className="form-input" value={config.paymentLink} onChange={e => setConfig(p => ({ ...p, paymentLink: e.target.value }))} placeholder="https://pay.stripe.com/..." /></div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Payment Instructions</label><textarea className="form-input" rows={2} value={config.paymentInstructions} onChange={e => setConfig(p => ({ ...p, paymentInstructions: e.target.value }))} placeholder="To complete your order, please pay here:" /></div>
          <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Confirmation Message</label><input className="form-input" value={config.confirmationMessage} onChange={e => setConfig(p => ({ ...p, confirmationMessage: e.target.value }))} /></div>
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button></div>
    </div>
  );
}

/* --- APPOINTMENT CONFIG --- */
function AppointmentConfigSection({ merchant }) {
  const [config, setConfig] = useState({ enabled: false, services: [], providers: [], slotDuration: 30, maxAdvanceDays: 30, workingHours: '09:00-17:00', workingDays: [1,2,3,4,5], bookingPageEnabled: false, confirmationMessage: 'Your appointment has been booked!', cancellationPolicy: '', sheetSpreadsheetId: '', sheetName: 'Appointments', paymentLink: '' });
  const [saving, setSaving] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [newService, setNewService] = useState({ name: '', duration: 30, price: 0 });
  const [editingServiceIndex, setEditingServiceIndex] = useState(null);
  const [editingService, setEditingService] = useState({ name: '', duration: 30, price: 0 });
  const [newProvider, setNewProvider] = useState({ name: '', title: '' });
  const [editingProviderIndex, setEditingProviderIndex] = useState(null);
  const [editingProvider, setEditingProvider] = useState({ name: '', title: '' });
  const [copied, setCopied] = useState(false);
  const bookingUrl = `${window.location.origin}/book/${merchant?._id}`;

  useEffect(() => {
    if (merchant?.appointmentConfig) setConfig(p => ({ ...p, ...merchant.appointmentConfig }));
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, [merchant]);

  const addService = () => {
    if (!newService.name.trim()) return;
    setConfig(p => ({ ...p, services: [...p.services, { ...newService }] }));
    setNewService({ name: '', duration: 30, price: 0 });
  };

  const startEditService = (index) => {
    const service = config.services[index];
    if (!service) return;
    setEditingServiceIndex(index);
    setEditingService({ ...service });
  };

  const cancelEditService = () => {
    setEditingServiceIndex(null);
    setEditingService({ name: '', duration: 30, price: 0 });
  };

  const saveEditedService = () => {
    if (editingServiceIndex === null || !editingService.name.trim()) return;
    const oldName = config.services[editingServiceIndex]?.name;
    setConfig(prev => {
      const services = prev.services.map((svc, idx) => idx === editingServiceIndex ? { ...editingService } : svc);
      const providers = prev.providers.map(provider => ({
        ...provider,
        services: (provider.services || []).map(serviceName => serviceName === oldName ? editingService.name : serviceName),
      }));
      return { ...prev, services, providers };
    });
    cancelEditService();
  };

  const startEditProvider = (index) => {
    const provider = config.providers[index];
    if (!provider) return;
    setEditingProviderIndex(index);
    setEditingProvider({ name: provider.name, title: provider.title });
  };

  const cancelEditProvider = () => {
    setEditingProviderIndex(null);
    setEditingProvider({ name: '', title: '' });
  };

  const saveEditedProvider = () => {
    if (editingProviderIndex === null || !editingProvider.name.trim()) return;
    setConfig(prev => ({
      ...prev,
      providers: prev.providers.map((prov, idx) => idx === editingProviderIndex ? { ...prov, ...editingProvider } : prov),
    }));
    cancelEditProvider();
  };

  const save = async () => { setSaving(true); try { await merchantAPI.updateBusinessType({ appointmentConfig: config }); toast.success('Saved'); } catch (_) {} finally { setSaving(false); } };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div><div className="card-title">Appointment / Booking Settings</div><p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>AI books appointments, checks availability, sends confirmations</p></div>
        <button className={`toggle ${config.enabled ? 'on' : ''}`} onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))} />
      </div>
      {config.enabled && (
        <>
          <div style={{ marginBottom: 18 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 8 }}>
              <div>
                <label className="form-label">Services</label>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Add the services customers can book. Use duration in minutes and the profile currency for prices.</div>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Current currency: <strong>{merchant?.currency || 'USD'}</strong></div>
            </div>
            {config.services.length > 0 ? (
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                {config.services.map((s, i) => (
                  editingServiceIndex === i ? (
                    <div key={i} style={{ display: 'grid', gap: 8, padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)' }}>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
                        <div>
                          <label className="form-label" style={{ marginBottom: 4 }}>Service name</label>
                          <input className="form-input" value={editingService.name} onChange={e => setEditingService(prev => ({ ...prev, name: e.target.value }))} />
                        </div>
                        <div>
                          <label className="form-label" style={{ marginBottom: 4 }}>Duration</label>
                          <input className="form-input" type="number" value={editingService.duration} onChange={e => setEditingService(prev => ({ ...prev, duration: parseInt(e.target.value) || 30 }))} />
                        </div>
                        <div>
                          <label className="form-label" style={{ marginBottom: 4 }}>Price</label>
                          <input className="form-input" type="number" value={editingService.price} onChange={e => setEditingService(prev => ({ ...prev, price: parseFloat(e.target.value) || 0 }))} />
                        </div>
                        <div style={{ display: 'flex', gap: 6 }}>
                          <button className="btn btn-primary btn-sm" style={{ height: 38 }} onClick={saveEditedService}>Save</button>
                          <button className="btn btn-ghost btn-sm" style={{ height: 38 }} onClick={cancelEditService}>Cancel</button>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 10, alignItems: 'center', padding: '10px 12px', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)' }}>
                      <div>
                        <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{s.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{s.duration} min · {merchant?.currency || 'USD'} {s.price}</div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => startEditService(i)}><Edit3 size={12} /></button>
                        <button className="btn btn-danger btn-sm" onClick={() => setConfig(p => ({ ...p, services: p.services.filter((_, idx) => idx !== i) }))}><Trash2 size={12} /></button>
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>No services added yet.</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
              <div>
                <label className="form-label">Service name</label>
                <input className="form-input" placeholder="e.g. Haircut" value={newService.name} onChange={e => setNewService(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Duration (min)</label>
                <input className="form-input" type="number" placeholder="30" value={newService.duration} onChange={e => setNewService(p => ({ ...p, duration: parseInt(e.target.value) || 30 }))} />
              </div>
              <div>
                <label className="form-label">Price ({merchant?.currency || 'USD'})</label>
                <input className="form-input" type="number" placeholder="0" value={newService.price} onChange={e => setNewService(p => ({ ...p, price: parseFloat(e.target.value) || 0 }))} />
              </div>
              <button className="btn btn-secondary btn-sm" onClick={addService} style={{ height: 38, alignSelf: 'flex-end' }}><Plus size={13} /> Add</button>
            </div>
          </div>
          {/* Providers / Staff */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16, marginBottom: 8 }}>
              <div>
                <label className="form-label">Providers / Staff</label>
                <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 4 }}>Add your team members and assign the services they can perform. Leave service assignment empty to allow all services.</div>
              </div>
              <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Example: Doctors, Stylists, Therapists</div>
            </div>
            {config.providers.length > 0 ? (
              <div style={{ display: 'grid', gap: 8, marginBottom: 12 }}>
                {config.providers.map((p, i) => (
                  editingProviderIndex === i ? (
                    <div key={i} style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)' }}>
                      <div style={{ display: 'grid', gap: 10, marginBottom: 10 }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                          <div>
                            <label className="form-label">Name</label>
                            <input className="form-input" value={editingProvider.name} onChange={e => setEditingProvider(prev => ({ ...prev, name: e.target.value }))} />
                          </div>
                          <div>
                            <label className="form-label">Title</label>
                            <input className="form-input" value={editingProvider.title} onChange={e => setEditingProvider(prev => ({ ...prev, title: e.target.value }))} />
                          </div>
                        </div>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button className="btn btn-primary btn-sm" onClick={saveEditedProvider}><Save size={12} /> Save</button>
                          <button className="btn btn-ghost btn-sm" onClick={cancelEditProvider}><X size={12} /> Cancel</button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {config.services.length > 0 ? config.services.map((s, si) => {
                          const assigned = (p.services || []).includes(s.name);
                          return (
                            <button key={si} onClick={() => {
                              const providers = [...config.providers];
                              const currentServices = providers[i].services || [];
                              providers[i] = { ...providers[i], services: assigned ? currentServices.filter(n => n !== s.name) : [...currentServices, s.name] };
                              setConfig(prev => ({ ...prev, providers }));
                            }}
                              style={{
                                padding: '5px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                                border: `1px solid ${assigned ? 'var(--primary)' : 'var(--border)'}`,
                                background: assigned ? 'var(--primary-light)' : 'transparent',
                                color: assigned ? 'var(--primary)' : 'var(--text-muted)',
                              }}>{s.name}</button>
                          );
                        }) : <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Add services above to assign providers.</div>}
                        {config.services.length > 0 && (p.services || []).length === 0 && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>All services</span>}
                      </div>
                    </div>
                  ) : (
                    <div key={i} style={{ padding: '12px', border: '1px solid var(--border)', borderRadius: 10, background: 'var(--bg)' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', marginBottom: 10 }}>
                        <div>
                          <div style={{ fontSize: '0.9rem', fontWeight: 600 }}>{p.title ? `${p.title} ` : ''}{p.name}</div>
                          <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginTop: 2 }}>{p.isActive !== false ? 'Active' : 'Inactive'}</div>
                        </div>
                        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                          <button className="btn btn-ghost btn-sm" onClick={() => startEditProvider(i)}><Edit3 size={12} /></button>
                          <button className={`toggle ${p.isActive !== false ? 'on' : ''}`} style={{ transform: 'scale(0.8)' }}
                            onClick={() => { const providers = [...config.providers]; providers[i] = { ...providers[i], isActive: !providers[i].isActive }; setConfig(prev => ({ ...prev, providers })); }} />
                          <button className="btn btn-danger btn-sm" onClick={() => setConfig(prev => ({ ...prev, providers: prev.providers.filter((_, idx) => idx !== i) }))}><Trash2 size={12} /></button>
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                        {config.services.length > 0 ? config.services.map((s, si) => {
                          const assigned = (p.services || []).includes(s.name);
                          return (
                            <button key={si} onClick={() => {
                              const providers = [...config.providers];
                              const currentServices = providers[i].services || [];
                              providers[i] = { ...providers[i], services: assigned ? currentServices.filter(n => n !== s.name) : [...currentServices, s.name] };
                              setConfig(prev => ({ ...prev, providers }));
                            }}
                              style={{
                                padding: '5px 12px', borderRadius: 8, fontSize: '0.78rem', fontWeight: 600, cursor: 'pointer',
                                border: `1px solid ${assigned ? 'var(--primary)' : 'var(--border)'}`,
                                background: assigned ? 'var(--primary-light)' : 'transparent',
                                color: assigned ? 'var(--primary)' : 'var(--text-muted)',
                              }}>{s.name}</button>
                          );
                        }) : <div style={{ fontSize: '0.82rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>Add services above to assign providers.</div>}
                        {config.services.length > 0 && (p.services || []).length === 0 && <span style={{ fontSize: '0.78rem', color: 'var(--text-muted)', fontStyle: 'italic' }}>All services</span>}
                      </div>
                    </div>
                  )
                ))}
              </div>
            ) : (
              <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginBottom: 12 }}>No providers added yet.</div>
            )}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: 8, alignItems: 'flex-end' }}>
              <div>
                <label className="form-label">Name</label>
                <input className="form-input" placeholder="Jane Doe" value={newProvider.name} onChange={e => setNewProvider(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div>
                <label className="form-label">Title</label>
                <input className="form-input" placeholder="Dr., Mr., Ms." value={newProvider.title} onChange={e => setNewProvider(p => ({ ...p, title: e.target.value }))} />
              </div>
              <button className="btn btn-secondary btn-sm" onClick={() => { if (!newProvider.name.trim()) return; setConfig(p => ({ ...p, providers: [...(p.providers || []), { ...newProvider, isActive: true, services: [] }] })); setNewProvider({ name: '', title: '' }); }} style={{ height: 38 }}> <Plus size={13} /> Add</button>
            </div>
          </div>

          {/* Booking Page Toggle */}
          <div style={{ padding: '14px 16px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)', marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div>
                <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>Public Booking Page</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Allow customers to book directly from a web page</div>
              </div>
              <button className={`toggle ${config.bookingPageEnabled ? 'on' : ''}`} onClick={() => setConfig(p => ({ ...p, bookingPageEnabled: !p.bookingPageEnabled }))} />
            </div>
            {config.bookingPageEnabled && (
              <div style={{ marginTop: 10 }}>
                <div style={{ fontSize: '0.78rem', fontWeight: 600, marginBottom: 4 }}>Your Booking URL:</div>
                <div onClick={() => { navigator.clipboard.writeText(bookingUrl); setCopied(true); setTimeout(() => setCopied(false), 2000); toast.success('Copied!'); }}
                  style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--card-bg)', border: '1px solid var(--border)', borderRadius: 8, cursor: 'pointer' }}>
                  <code style={{ flex: 1, fontSize: '0.75rem', color: 'var(--primary)', wordBreak: 'break-all' }}>{bookingUrl}</code>
                  <span style={{ fontSize: '0.72rem', color: copied ? 'var(--green)' : 'var(--text-muted)', fontWeight: 600 }}>{copied ? 'Copied!' : 'Copy'}</span>
                </div>
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 6 }}>Share this link with customers or embed it on your website.</div>
                <div style={{ marginTop: 14 }}>
                  <label className="form-label" style={{ fontSize: '0.82rem', fontWeight: 600 }}>Booking Message</label>
                  <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 6 }}>Customize the message sent to customers along with the booking link.</div>
                  <textarea
                    className="form-input"
                    rows={2}
                    placeholder="Sure thing! Please use this link to book your appointment:"
                    value={config.bookingMessage || ''}
                    onChange={e => setConfig(p => ({ ...p, bookingMessage: e.target.value }))}
                    style={{ resize: 'vertical', fontSize: '0.83rem' }}
                  />
                </div>
              </div>
            )}
          </div>

          {/* Working Days */}
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Working Days</label>
            <div style={{ display: 'flex', gap: 6 }}>
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, i) => {
                const active = (config.workingDays || [1,2,3,4,5]).includes(i);
                return (
                  <button key={i} onClick={() => {
                    const days = config.workingDays || [1,2,3,4,5];
                    setConfig(p => ({ ...p, workingDays: active ? days.filter(d => d !== i) : [...days, i] }));
                  }}
                    style={{
                      padding: '6px 10px', borderRadius: 8, border: `2px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
                      background: active ? 'var(--primary-light)' : 'transparent', color: active ? 'var(--primary)' : 'var(--text-muted)',
                      fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer',
                    }}>{day}</button>
                );
              })}
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group"><label className="form-label">Working Hours</label><input className="form-input" value={config.workingHours} onChange={e => setConfig(p => ({ ...p, workingHours: e.target.value }))} placeholder="09:00-17:00" /></div>
            <div className="form-group"><label className="form-label">Slot Duration (min)</label><input className="form-input" type="number" value={config.slotDuration} onChange={e => setConfig(p => ({ ...p, slotDuration: parseInt(e.target.value) || 30 }))} /></div>
            <div className="form-group"><label className="form-label">Max Advance Days</label><input className="form-input" type="number" value={config.maxAdvanceDays} onChange={e => setConfig(p => ({ ...p, maxAdvanceDays: parseInt(e.target.value) || 30 }))} /></div>
            <div className="form-group"><label className="form-label">Payment Link</label><input className="form-input" value={config.paymentLink} onChange={e => setConfig(p => ({ ...p, paymentLink: e.target.value }))} placeholder="https://..." /></div>
            <div className="form-group"><label className="form-label">Save To Spreadsheet</label><select className="form-input" value={config.sheetSpreadsheetId} onChange={e => setConfig(p => ({ ...p, sheetSpreadsheetId: e.target.value }))}><option value="">Select...</option>{spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Sheet Tab</label><input className="form-input" value={config.sheetName} onChange={e => setConfig(p => ({ ...p, sheetName: e.target.value }))} placeholder="Appointments" /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Confirmation Message</label><input className="form-input" value={config.confirmationMessage} onChange={e => setConfig(p => ({ ...p, confirmationMessage: e.target.value }))} /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Cancellation Policy</label><textarea className="form-input" rows={2} value={config.cancellationPolicy} onChange={e => setConfig(p => ({ ...p, cancellationPolicy: e.target.value }))} placeholder="e.g. Cancellations must be made 24 hours in advance" /></div>
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button></div>
    </div>
  );
}

/* --- LEAD CONFIG --- */
function LeadConfigSection({ merchant }) {
  const [config, setConfig] = useState({ enabled: false, requiredFields: ['name', 'phone', 'email'], qualificationQuestions: [], confirmationMessage: 'Thank you! Our team will contact you shortly.', notifyEmail: '', sheetSpreadsheetId: '', sheetName: 'Leads' });
  const [saving, setSaving] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [newField, setNewField] = useState('');
  const [newQ, setNewQ] = useState('');

  useEffect(() => {
    if (merchant?.leadConfig) setConfig(p => ({ ...p, ...merchant.leadConfig }));
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, [merchant]);

  const save = async () => { setSaving(true); try { await merchantAPI.updateBusinessType({ leadConfig: config }); toast.success('Saved'); } catch (_) {} finally { setSaving(false); } };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div><div className="card-title">Lead / Inquiry Settings</div><p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>AI collects customer info, qualifies leads, saves to sheet</p></div>
        <button className={`toggle ${config.enabled ? 'on' : ''}`} onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))} />
      </div>
      {config.enabled && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Required Fields to Collect</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {config.requiredFields.map((f, i) => (
                <span key={i} className="badge badge-blue" style={{ cursor: 'pointer' }} onClick={() => setConfig(p => ({ ...p, requiredFields: p.requiredFields.filter((_, idx) => idx !== i) }))}>
                  {f} x
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="e.g. budget, company, location" value={newField} onChange={e => setNewField(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newField.trim()) { setConfig(p => ({ ...p, requiredFields: [...p.requiredFields, newField.trim()] })); setNewField(''); }}} />
              <button className="btn btn-secondary btn-sm" onClick={() => { if (newField.trim()) { setConfig(p => ({ ...p, requiredFields: [...p.requiredFields, newField.trim()] })); setNewField(''); } }}><Plus size={13} /></button>
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Qualification Questions (optional)</label>
            {config.qualificationQuestions.map((q, i) => (
              <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 4 }}>
                <span style={{ flex: 1, fontSize: '0.82rem' }}>{q}</span>
                <button className="btn btn-danger btn-sm" onClick={() => setConfig(p => ({ ...p, qualificationQuestions: p.qualificationQuestions.filter((_, idx) => idx !== i) }))}><Trash2 size={12} /></button>
              </div>
            ))}
            <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
              <input className="form-input" placeholder="e.g. What is your budget range?" value={newQ} onChange={e => setNewQ(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newQ.trim()) { setConfig(p => ({ ...p, qualificationQuestions: [...p.qualificationQuestions, newQ.trim()] })); setNewQ(''); }}} />
              <button className="btn btn-secondary btn-sm" onClick={() => { if (newQ.trim()) { setConfig(p => ({ ...p, qualificationQuestions: [...p.qualificationQuestions, newQ.trim()] })); setNewQ(''); } }}><Plus size={13} /></button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group"><label className="form-label">Notify Email</label><input className="form-input" value={config.notifyEmail} onChange={e => setConfig(p => ({ ...p, notifyEmail: e.target.value }))} placeholder="team@company.com" /></div>
            <div className="form-group"><label className="form-label">Save To Spreadsheet</label><select className="form-input" value={config.sheetSpreadsheetId} onChange={e => setConfig(p => ({ ...p, sheetSpreadsheetId: e.target.value }))}><option value="">Select...</option>{spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Sheet Tab</label><input className="form-input" value={config.sheetName} onChange={e => setConfig(p => ({ ...p, sheetName: e.target.value }))} placeholder="Leads" /></div>
            <div className="form-group"><label className="form-label">Confirmation Message</label><input className="form-input" value={config.confirmationMessage} onChange={e => setConfig(p => ({ ...p, confirmationMessage: e.target.value }))} /></div>
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button></div>
    </div>
  );
}

/* --- SUPPORT CONFIG --- */
function SupportConfigSection({ merchant }) {
  const [config, setConfig] = useState({ enabled: false, categories: [], escalationMessage: "I'm connecting you to a human for further assistance.", ticketPrefix: 'TKT', sheetSpreadsheetId: '', sheetName: 'Tickets' });
  const [saving, setSaving] = useState(false);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [newCat, setNewCat] = useState('');

  useEffect(() => {
    if (merchant?.supportConfig) setConfig(p => ({ ...p, ...merchant.supportConfig }));
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, [merchant]);

  const save = async () => { setSaving(true); try { await merchantAPI.updateBusinessType({ supportConfig: config }); toast.success('Saved'); } catch (_) {} finally { setSaving(false); } };

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
        <div><div className="card-title">Support / Helpdesk Settings</div><p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>AI resolves issues, creates tickets when escalation needed</p></div>
        <button className={`toggle ${config.enabled ? 'on' : ''}`} onClick={() => setConfig(p => ({ ...p, enabled: !p.enabled }))} />
      </div>
      {config.enabled && (
        <>
          <div style={{ marginBottom: 16 }}>
            <label className="form-label">Support Categories</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
              {config.categories.map((c, i) => (
                <span key={i} className="badge badge-blue" style={{ cursor: 'pointer' }} onClick={() => setConfig(p => ({ ...p, categories: p.categories.filter((_, idx) => idx !== i) }))}>
                  {c} x
                </span>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input className="form-input" placeholder="e.g. billing, technical, general" value={newCat} onChange={e => setNewCat(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && newCat.trim()) { setConfig(p => ({ ...p, categories: [...p.categories, newCat.trim()] })); setNewCat(''); }}} />
              <button className="btn btn-secondary btn-sm" onClick={() => { if (newCat.trim()) { setConfig(p => ({ ...p, categories: [...p.categories, newCat.trim()] })); setNewCat(''); } }}><Plus size={13} /></button>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group"><label className="form-label">Ticket Prefix</label><input className="form-input" value={config.ticketPrefix} onChange={e => setConfig(p => ({ ...p, ticketPrefix: e.target.value }))} placeholder="TKT" /></div>
            <div className="form-group"><label className="form-label">Save To Spreadsheet</label><select className="form-input" value={config.sheetSpreadsheetId} onChange={e => setConfig(p => ({ ...p, sheetSpreadsheetId: e.target.value }))}><option value="">Select...</option>{spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}</select></div>
            <div className="form-group"><label className="form-label">Sheet Tab</label><input className="form-input" value={config.sheetName} onChange={e => setConfig(p => ({ ...p, sheetName: e.target.value }))} placeholder="Tickets" /></div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}><label className="form-label">Escalation Message</label><textarea className="form-input" rows={2} value={config.escalationMessage} onChange={e => setConfig(p => ({ ...p, escalationMessage: e.target.value }))} /></div>
          </div>
        </>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}><button className="btn btn-primary" onClick={save} disabled={saving}><Save size={14} /> {saving ? 'Saving...' : 'Save'}</button></div>
    </div>
  );
}

/* --- DATA SHEET SELECTOR --- */
function DataSheetSelector({ merchant }) {
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [selected, setSelected] = useState({ spreadsheetId: '', spreadsheetName: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (merchant?.dataSheet) setSelected({ spreadsheetId: merchant.dataSheet.spreadsheetId || '', spreadsheetName: merchant.dataSheet.spreadsheetName || '' });
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, [merchant]);

  const save = async () => {
    setSaving(true);
    try {
      await merchantAPI.updateDataSheet(selected);
      toast.success('Data sheet connected');
    } catch (_) {}
    finally { setSaving(false); }
  };

  return (
    <div className="card" style={{ marginBottom: 20 }}>
      <div>
        <div className="card-title">Data Sheet</div>
        <p style={{ fontSize: '0.82rem', color: 'var(--text-muted)', marginTop: 2 }}>
          Connect a Google Spreadsheet to save all data. Orders, appointments, leads, and tickets are saved in separate tabs automatically.
        </p>
      </div>
      <div style={{ display: 'flex', gap: 10, marginTop: 14, alignItems: 'end' }}>
        <div className="form-group" style={{ flex: 1, marginBottom: 0 }}>
          <label className="form-label">Google Spreadsheet</label>
          <select className="form-input" value={selected.spreadsheetId}
            onChange={e => {
              const ss = spreadsheets.find(s => s.id === e.target.value);
              setSelected({ spreadsheetId: e.target.value, spreadsheetName: ss?.name || '' });
            }}>
            <option value="">Select a spreadsheet...</option>
            {spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </div>
        <button className="btn btn-primary" onClick={save} disabled={saving} style={{ height: 38 }}>
          <Save size={14} /> {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
      {selected.spreadsheetId && (
        <div className="alert alert-success" style={{ marginTop: 12, marginBottom: 0 }}>
          <Info size={13} />
          <span style={{ fontSize: '0.78rem' }}>
            Connected to <strong>{selected.spreadsheetName}</strong>. Tabs created automatically: Orders, Appointments, Leads, Tickets
          </span>
        </div>
      )}
      {!selected.spreadsheetId && spreadsheets.length === 0 && (
        <div className="alert alert-info" style={{ marginTop: 12, marginBottom: 0 }}>
          <Info size={13} />
          <span style={{ fontSize: '0.78rem' }}>Connect Google Sheets first in the <strong>Integrations</strong> page</span>
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// Named Sheets Section
// ─────────────────────────────────────────────
const EMPTY_NAMED_SHEET = { name: '', spreadsheetId: '', sheetName: 'Sheet1', description: '', fields: '' };

function NamedSheetsSection() {
  const [sheets, setSheets] = useState([]);
  const [spreadsheets, setSpreadsheets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_NAMED_SHEET });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    merchantAPI.getNamedSheets().then(({ data }) => setSheets(data.data || [])).catch(() => {}).finally(() => setLoading(false));
    integrationsAPI.listSpreadsheets().then(({ data }) => setSpreadsheets(data.data || [])).catch(() => {});
  }, []);

  const openAdd = () => { setForm({ ...EMPTY_NAMED_SHEET }); setEditingId(null); setShowForm(true); };
  const openEdit = (s) => { setForm({ name: s.name, spreadsheetId: s.spreadsheetId, sheetName: s.sheetName || 'Sheet1', description: s.description || '', fields: s.fields || '' }); setEditingId(s._id); setShowForm(true); };

  const handleSave = async () => {
    if (!form.name.trim()) return toast.error('Sheet name is required');
    if (!form.spreadsheetId.trim()) return toast.error('Select a spreadsheet');
    setSaving(true);
    try {
      if (editingId) {
        const { data } = await merchantAPI.updateNamedSheet(editingId, form);
        setSheets(data.data);
        toast.success('Sheet updated');
      } else {
        const { data } = await merchantAPI.addNamedSheet(form);
        setSheets(data.data);
        toast.success('Sheet added');
      }
      setShowForm(false);
      setEditingId(null);
    } catch (_) {}
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this sheet destination?')) return;
    try {
      const { data } = await merchantAPI.deleteNamedSheet(id);
      setSheets(data.data);
      toast.success('Removed');
    } catch (_) {}
  };

  const promptSnippet = sheets.map(s =>
    `If ${s.description || `related to ${s.name}`}, collect ${s.fields || 'name, email, phone, notes'} and save to "${s.name}" sheet.`
  ).join('\n');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>

      {/* Header card */}
      <div className="card">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
              <div style={{ width: 36, height: 36, borderRadius: 9, background: '#f0fdf4', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FileSpreadsheet size={18} style={{ color: '#16a34a' }} />
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: '1rem' }}>Named Sheet Destinations</div>
                <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)' }}>Route AI-collected data to specific sheets based on conversation context</div>
              </div>
            </div>
            <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', lineHeight: 1.6 }}>
              Add multiple Google Sheet destinations — give each one a <strong>name</strong>, <strong>trigger description</strong>, and <strong>fields to collect</strong>.
              The AI will automatically save to the right sheet based on what the user is asking about.
              Reference them by name in your prompt (e.g. <em>"save job inquiries to the Job Applications sheet"</em>).
            </div>
          </div>
          <button className="btn btn-primary btn-sm" onClick={openAdd} style={{ flexShrink: 0 }}>
            <Plus size={14} /> Add Sheet
          </button>
        </div>
      </div>

      {/* Sheet list */}
      {loading ? (
        <div className="flex-center" style={{ padding: 40 }}><div className="spinner" /></div>
      ) : sheets.length === 0 ? (
        <div className="card" style={{ textAlign: 'center', padding: 40 }}>
          <FileSpreadsheet size={36} style={{ color: 'var(--text-muted)', marginBottom: 12 }} />
          <h3 style={{ marginBottom: 6 }}>No sheet destinations yet</h3>
          <p style={{ color: 'var(--text-muted)', fontSize: '0.82rem', marginBottom: 16 }}>
            Add a destination to let the AI automatically save collected data to the right Google Sheet.
          </p>
          <button className="btn btn-primary" onClick={openAdd}><Plus size={14} /> Add First Sheet</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {sheets.map((s, idx) => (
            <div key={s._id} className="card" style={{ padding: '16px 20px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ width: 36, height: 36, borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <span style={{ fontSize: '0.75rem', fontWeight: 800, color: '#16a34a' }}>#{idx + 1}</span>
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: '0.95rem' }}>{s.name}</span>
                    <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: 20, background: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0', fontWeight: 600 }}>
                      Tab: {s.sheetName || 'Sheet1'}
                    </span>
                  </div>
                  {s.description && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                      <strong>Trigger:</strong> {s.description}
                    </div>
                  )}
                  {s.fields && (
                    <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', marginBottom: 4 }}>
                      <strong>Collect:</strong> {s.fields}
                    </div>
                  )}
                  <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', fontFamily: 'monospace', marginTop: 2 }}>
                    ID: {s.spreadsheetId}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => openEdit(s)}><Edit3 size={12} /></button>
                  <button className="btn btn-danger btn-sm" onClick={() => handleDelete(s._id)}><Trash2 size={12} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Prompt snippet helper */}
      {sheets.length > 0 && (
        <div className="card" style={{ background: '#fafaf9', border: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <Sparkles size={15} style={{ color: 'var(--primary)' }} />
            <span style={{ fontWeight: 700, fontSize: '0.88rem' }}>Auto-generated Prompt Snippet</span>
          </div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 8 }}>
            Copy this into your AI prompt so the AI knows when and what to save:
          </div>
          <div style={{ position: 'relative' }}>
            <pre style={{ margin: 0, padding: '12px 14px', background: '#1e293b', color: '#94a3b8', borderRadius: 8, fontSize: '0.75rem', lineHeight: 1.7, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {promptSnippet}
            </pre>
            <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(promptSnippet); toast.success('Copied!'); }}
              style={{ position: 'absolute', top: 8, right: 8, fontSize: '0.7rem', padding: '3px 8px' }}>
              Copy
            </button>
          </div>
          <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 8 }}>
            Paste this into your prompt in the <strong>Prompts</strong> tab, then the AI will automatically collect and route data to the right sheet.
          </div>
        </div>
      )}

      {/* Add/Edit modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal" style={{ maxWidth: 500 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3 style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <FileSpreadsheet size={16} style={{ color: '#16a34a' }} />
                {editingId ? 'Edit Sheet Destination' : 'Add Sheet Destination'}
              </h3>
              <button className="btn-icon" onClick={() => setShowForm(false)}><X size={18} /></button>
            </div>
            <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              <div>
                <label className="form-label">Destination Name *</label>
                <input className="form-input" placeholder='e.g. Job Applications, Finance Leads, Customer Feedback'
                  value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  This name is used in the AI prompt. Use clear, descriptive names.
                </div>
              </div>

              <div>
                <label className="form-label">Google Spreadsheet *</label>
                {spreadsheets.length > 0 ? (
                  <select className="form-input" value={form.spreadsheetId}
                    onChange={e => setForm(p => ({ ...p, spreadsheetId: e.target.value }))}>
                    <option value="">Select a spreadsheet...</option>
                    {spreadsheets.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                  </select>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    <input className="form-input" placeholder="Paste Google Spreadsheet ID"
                      value={form.spreadsheetId} onChange={e => setForm(p => ({ ...p, spreadsheetId: e.target.value }))} />
                    <div className="alert alert-info" style={{ marginTop: 0 }}>
                      <Info size={13} />
                      <span style={{ fontSize: '0.75rem' }}>Connect Google Sheets in <strong>Integrations</strong> to see a picker, or paste the Sheet ID manually.</span>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="form-label">Sheet Tab Name</label>
                <input className="form-input" placeholder='e.g. Applications, Leads, Sheet1'
                  value={form.sheetName} onChange={e => setForm(p => ({ ...p, sheetName: e.target.value }))} />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  The tab name inside the spreadsheet. Must match exactly.
                </div>
              </div>

              <div>
                <label className="form-label">Trigger Description</label>
                <input className="form-input" placeholder='e.g. When user applies for a job position'
                  value={form.description} onChange={e => setForm(p => ({ ...p, description: e.target.value }))} />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  Tells the AI when to use this sheet. Used to generate your prompt snippet.
                </div>
              </div>

              <div>
                <label className="form-label">Fields to Collect</label>
                <input className="form-input" placeholder='e.g. name, email, phone, position applied, experience'
                  value={form.fields} onChange={e => setForm(p => ({ ...p, fields: e.target.value }))} />
                <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)', marginTop: 4 }}>
                  List the info the AI should ask for before saving. Comma-separated.
                </div>
              </div>

              {/* Preview */}
              {form.name && (
                <div style={{ padding: '10px 12px', borderRadius: 8, background: '#f0fdf4', border: '1px solid #bbf7d0' }}>
                  <div style={{ fontSize: '0.72rem', fontWeight: 700, color: '#16a34a', marginBottom: 4 }}>Prompt snippet preview:</div>
                  <div style={{ fontSize: '0.76rem', color: '#166534', lineHeight: 1.6 }}>
                    {`If ${form.description || `related to ${form.name}`}, collect ${form.fields || 'name, email, phone, notes'} and save to "${form.name}" sheet.`}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowForm(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingId ? 'Update' : 'Add Sheet'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
